import { useState, type ClipboardEvent } from 'react'
import { Input } from 'antd'
import { AUTHENTICATOR_CODE_LENGTH, isCompleteAuthenticatorCode } from '../authenticatorCode'

type AuthenticatorCodeInputProps = {
  /**
   * The current boxes as one string, with a space for each empty box, so a code with a gap
   * never looks complete. Check it with `isCompleteAuthenticatorCode`.
   */
  onChange: (value: string) => void
  /** Called once all six boxes hold digits (typed, pasted or autofilled). */
  onComplete?: (value: string) => void
  disabled?: boolean
  autoFocus?: boolean
  /** Change it after a failed attempt: the boxes remount empty with focus on the first one. */
  resetKey?: number
}

/**
 * The 6-digit authenticator code boxes used at sign-in and in the Profile setup/disable modals.
 *
 * Uncontrolled on purpose: feeding the joined value back in closes the gap when a middle digit
 * is deleted, so the later digits shift left. antd also cuts pasted text to six characters
 * before formatting, which would turn a pasted "123 456" into "12345"; pastes are read here.
 */
export default function AuthenticatorCodeInput({
  onChange,
  onComplete,
  disabled,
  autoFocus,
  resetKey = 0,
}: AuthenticatorCodeInputProps) {
  // A paste remounts the boxes pre-filled; a new resetKey starts them empty again.
  const [pasted, setPasted] = useState<{ resetKey: number; count: number; value: string }>({
    resetKey,
    count: 0,
    value: '',
  })
  const current = pasted.resetKey === resetKey ? pasted : { resetKey, count: pasted.count, value: '' }

  const report = (value: string) => {
    onChange(value)
    if (isCompleteAuthenticatorCode(value)) onComplete?.(value)
  }

  const onPasteCapture = (event: ClipboardEvent<HTMLDivElement>) => {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '')
    if (digits.length !== AUTHENTICATOR_CODE_LENGTH) return
    event.preventDefault()
    setPasted({ resetKey, count: current.count + 1, value: digits })
    report(digits)
  }

  return (
    <div onPasteCapture={onPasteCapture}>
      <Input.OTP
        key={`${resetKey}-${current.count}`}
        aria-label="Authentication code"
        length={AUTHENTICATOR_CODE_LENGTH}
        defaultValue={current.value}
        autoFocus={autoFocus && !current.value}
        disabled={disabled}
        // Digits only. Spaces stay: antd uses them for empty boxes while formatting.
        formatter={(next) => next.replace(/[^\d ]/g, '')}
        onInput={(cells) => {
          const padded = Array.from({ length: AUTHENTICATOR_CODE_LENGTH }, (_, i) => (cells[i] && cells[i] !== ' ' ? cells[i] : ' '))
          report(padded.join(''))
        }}
        inputMode="numeric"
        autoComplete="one-time-code"
      />
    </div>
  )
}
